import { FC, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { PolicyFixInput } from '../../../types/issues-types';
import InputResolver from './inputs/InputResolver';
import ButtonGroup from '../../../../../common/components/ButtonGroup';
import { useSnapshot } from 'valtio';
import IssuesStore from '../../../../active-issues/stores/issues-store';
import { resolveSourceControlType } from '../../../../../quick-fix/utils/pull-request-utils';
import { addPolicyFix } from '../../../actions/policy-fix-actions';
import ModalConfirmationDialog from '../../../../../common/components/ModalConfirmationDialog';
import Message from '../../../../../common/components/Message';
import PolicyFixConfirmation from './PolicyFixConfirmation';
const PolicyFixContainer: FC<
  React.PropsWithChildren<PolicyFixContainerProps>
> = ({ settingType, inputs, onClose, description, warning, confirmation }) => {
  const { selectedIssue } = useSnapshot(IssuesStore);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [variables, setVariables] = useState(inputs);
  const [fixAvailable, setFixAvailable] = useState(true);
  const openConfirmationModal = useCallback(
    () => setConfirmationOpen(true),
    [],
  );
  const closeConfirmationModal = useCallback(
    () => setConfirmationOpen(false),
    [],
  );
  const onActionButtonClick = useCallback(async () => {
    closeConfirmationModal();
    await addPolicyFix(
      variables,
      settingType,
      resolveSourceControlType(selectedIssue?.app.type!),
    );
    onClose();
  }, [
    closeConfirmationModal,
    onClose,
    selectedIssue?.app.type,
    settingType,
    variables,
  ]);

  const onChange = (name: string, values: string | string[]) => {
    values = Array.isArray(values) ? values : [values];
    const inputIndex = inputs.findIndex(i => i.name === name);
    const input = inputs[inputIndex];
    setFixAvailable(true);
    if (
      input.minSelect &&
      input.minSelect !== -1 &&
      values.length < input.minSelect
    ) {
      setFixAvailable(false);
    }
    if (
      input.maxSelect &&
      input.maxSelect !== -1 &&
      values.length > input.maxSelect
    ) {
      setFixAvailable(false);
    }

    const newInputs = [...variables];
    newInputs[inputIndex] = {
      ...variables[inputIndex],
      options: inputs[inputIndex].options.map(o => ({
        ...o,
        selected: values.includes(o.name),
      })),
    };
    setVariables(newInputs);
  };

  return (
    <Box display='flex' height='100%' flexDirection='column' gap={1}>
      {description && <Message message={description} />}
      <Box
        display='flex'
        flexDirection='column'
        height='100%'
        mt={2}
        justifyContent='space-between'>
        <Box
          display='flex'
          flexDirection='column'
          maxHeight={400}
          gap={1}
          overflow='auto'>
          {variables.map(input => (
            <Box m={1} key={input.name}>
              <InputResolver input={input} onChange={onChange} />
            </Box>
          ))}
        </Box>
      </Box>
      {warning && <Message message={warning} variant='warning' />}
      <ButtonGroup
        size='small'
        onCancel={onClose}
        onClick={openConfirmationModal}
        buttonCancelLabel='close'
        buttonCancelTooltip=''
        buttonActionLabel='Fix'
        buttonActionTooltip=''
        buttonActionColor='primary'
        isActionDisabled={!fixAvailable}
      />
      <ModalConfirmationDialog
        title={'Confirm Fix Action'}
        dialogBody={
          <PolicyFixConfirmation
            variables={variables}
            confirmation={confirmation}
          />
        }
        confirmationButtonText={'Confirm'}
        cancelButtonText={'Cancel'}
        isOpen={confirmationOpen}
        onConfirmation={onActionButtonClick}
        onCloseConfirmationModal={closeConfirmationModal}
      />
    </Box>
  );
};

export interface PolicyFixContainerProps {
  inputs: PolicyFixInput[];
  settingType: string;
  warning?: string;
  description?: string;
  onClose: () => void;
  confirmation?: string;
}

export default PolicyFixContainer;
